/**
 * CountBadge — telleboblen (#152).
 *
 * Trukket ut av NavShell sin `ApprovalBadge`, og samme boble som sitter i en
 * valgt filterchip («Mine 2» i Oppgaver-skissen): fylt lime (`bg-fab` +
 * `text-on-fab`, likt i begge temaer).
 *
 * Størrelser:
 *   - sm  18 px, for et merke over et ikon (bunnmenyen)
 *   - md  32 px, inne i en Chip
 *
 * Teller 0 eller mindre gir ingenting. Tallet er synlig tekst; `label` gir
 * skjermlesere hele setningen («3 venter på godkjenning») i stedet for et
 * nakent tall.
 */
import type { ReactNode } from 'react';

type CountBadgeSize = 'sm' | 'md';

const SIZE_CLASS: Record<CountBadgeSize, string> = {
  sm: 'h-4.5 min-w-4.5 px-1 text-badge',
  md: 'h-8 min-w-8 px-2 text-control',
};

interface CountBadgeProps {
  count: number;
  size?: CountBadgeSize;
  /** Tilgjengelig navn; uten det leses bare tallet. */
  label?: string;
  className?: string;
  'data-testid'?: string;
}

export function CountBadge({
  count,
  size = 'md',
  label,
  className,
  'data-testid': testId = 'count-badge',
}: CountBadgeProps): ReactNode {
  if (count <= 0) return null;
  return (
    <span
      data-testid={testId}
      aria-label={label}
      role={label ? 'img' : undefined}
      className={[
        'inline-flex shrink-0 items-center justify-center rounded-pill bg-fab font-extrabold leading-none text-on-fab tnum',
        SIZE_CLASS[size],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {count}
    </span>
  );
}
